import * as styles from "./BarChart.css";

import { clsx } from "clsx";
import type { JSX } from "react";
import { useMemo } from "react";

import { vars } from "@frontend/styles";
import { barY, defineChart, group } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import { chartAnimation } from "../shared/animation";
import type { BarChartProps } from "./BarChart.types";
import { CHART_HEIGHT, CHART_PALETTE } from "./constants";

interface BarChartRow {
  x: string | number;
  series: string;
  value: number;
}

export function BarChart<TDatum extends Record<string, unknown>>({
  data,
  x,
  series,
  ariaLabel,
  height = CHART_HEIGHT,
  valueLabel,
  valueFormat,
  locale,
  animate = false,
  className,
  ...props
}: BarChartProps<TDatum>): JSX.Element {
  const formatter = useMemo(
    () => new Intl.NumberFormat(locale, valueFormat),
    [locale, valueFormat],
  );

  const rows = useMemo<BarChartRow[]>(
    () =>
      data.flatMap((datum) =>
        series.map((item) => ({
          x: datum[x] as string | number,
          series: item.label,
          value: datum[item.key] as number,
        })),
      ),
    [data, series, x],
  );

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          barY(rows, {
            x: "x",
            y: "value",
            color: "series",
            z: "series",
            layout: series.length > 1 ? group({ padding: 0.2 }) : undefined,
          }),
        ],
        scales: {
          x: {
            scale: () => scaleBand().padding(0.18),
          },
          y: {
            scale: scaleLinear,
            nice: true,
            grid: true,
            axis: {
              label: valueLabel,
              ticks: { format: (value) => formatter.format(value) },
            },
          },
        },
        theme: {
          muted: vars.color.textMuted,
          grid: vars.color.borderLight,
          palette: CHART_PALETTE,
        },
        svgAnimation: chartAnimation(animate),
        tooltip,
      }),
    [rows, series.length, valueLabel, formatter, animate],
  );

  return (
    <div
      data-slot="bar-chart"
      className={clsx(styles.root, className)}
      {...props}
    >
      <Chart
        className={styles.host}
        definition={definition}
        height={height}
        ariaLabel={ariaLabel}
      />
      {series.length > 1 && (
        <ul className={styles.legend}>
          {series.map((item, index) => (
            <li key={item.key} className={styles.legendItem}>
              <span
                className={styles.swatch}
                style={{
                  backgroundColor: CHART_PALETTE[index % CHART_PALETTE.length],
                }}
              />
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
